import React, { useState, useRef, useEffect } from 'react';
import { MessageCircleIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon } from 'lucide-react';
import { AiIcon } from './AIInsightsDrawer';

interface DashboardAssistantBarProps {
  /** Called with the trimmed text when the user submits a question. */
  onSubmit: (text: string) => void;
  /** Opens the assistant drawer without sending a message. */
  onOpen?: () => void;
  /** Optional starter prompts, shown in a collapsible list. */
  suggestions?: string[];
}

// Bottom-center composer that hands off the typed question to the AI Assistant.
// NOTE: when porting to another project, adjust the `md:pl-80` offset below to
// match that project's fixed sidebar width (or remove it if there is none) so the
// bar stays centered over the content area.
const DashboardAssistantBar: React.FC<DashboardAssistantBarProps> = ({ onSubmit, onOpen, suggestions = [] }) => {
  const [value, setValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Collapse the suggestions list when a click lands outside the card.
  useEffect(() => {
    if (!showSuggestions) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setValue('');
  };

  return (
    // Fixed to the viewport, centered over the content area (md:pl-80 mirrors the
    // sidebar width in Layout). pointer-events-none lets clicks pass through the
    // gutter; the card itself re-enables them.
    <div className="fixed bottom-4 inset-x-0 z-30 md:pl-80 pointer-events-none">
      <div className="flex justify-center px-4">
        <div ref={cardRef} className="pointer-events-auto w-full max-w-md bg-white border border-gray-100 transform transition-all shadow hover:shadow-lg p-4">
          {/* Collapsible starter prompts */}
          {suggestions.length > 0 && showSuggestions && (
            <div className="mb-3 space-y-1.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => { onSubmit(s); setShowSuggestions(false); }}
                  className="w-full text-left text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded px-3 py-2 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary-600 flex items-center justify-center">
              <AiIcon />
            </div>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              placeholder="Ask me about your data..."
              className="flex-1 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white transition-colors placeholder-gray-400"
            />
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-xs text-gray-500">
              <MessageCircleIcon className="h-3.5 w-3.5" />
              Press Enter to send
            </p>
            <div className="flex items-center gap-3 flex-shrink-0">
              {suggestions.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowSuggestions((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <SparklesIcon className="h-3.5 w-3.5" />
                  Suggestions
                  {showSuggestions
                    ? <ChevronDownIcon className="h-3.5 w-3.5" />
                    : <ChevronUpIcon className="h-3.5 w-3.5" />}
                </button>
              )}
              {onOpen && (
                <button
                  type="button"
                  onClick={onOpen}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                >
                  Open assistant
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardAssistantBar;
